import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react/hooks';
import { GET_ORG_PAGE_QUERY } from '../../graphql/organization';

type OrgOption = { id: string; name: string };

/**
 * Fetches organizations the user is onboarded to on POS (via the shared
 * graphql-server `me.organizationPage` resolver) and streams each page to
 * `onBatch`. Pagination continues until the server stops returning a cursor.
 *
 * `shouldContinue` is checked before every request and after every response
 * so that callers can abort recursion on unmount or re-trigger, preventing
 * stale state updates and unnecessary network calls.
 *
 * This replaces the previous SSO `/userinfo` fetch (`useSsoProfile`) for
 * device-selection. The SSO endpoint returns every org the user has SSO
 * access to, including orgs that have not been onboarded to POS — those
 * would appear in the picker but contain no venues/stores/devices.
 */
export const useOrgOptions = () => {
  const client = useApolloClient();

  const fetchPage = useCallback(
    async (
      onBatch: (batch: OrgOption[]) => void,
      shouldContinue: () => boolean,
      cursor?: string,
    ): Promise<void> => {
      if (!shouldContinue()) return;
      const res = await client.query<{
        me: {
          organizationPage: {
            organizations: OrgOption[];
            cursor?: string;
          };
        };
      }>({
        query: GET_ORG_PAGE_QUERY,
        variables: { cursor, limit: 100 },
        context: {
          headers: { organization: '', store: '', venue: '', device: '' },
        },
        fetchPolicy: 'network-only',
      });
      if (!shouldContinue()) return;
      const page = res?.data?.me?.organizationPage;
      if (!page?.organizations?.length) return;
      const batch = page.organizations
        .filter((org): org is { id: string; name: string } =>
          Boolean(org.id && org.name),
        )
        .map(org => ({ id: org.id, name: org.name }));
      if (batch.length) onBatch(batch);
      if (page.cursor) await fetchPage(onBatch, shouldContinue, page.cursor);
    },
    [client],
  );

  const getOrgAsOptionsOfUser = useCallback(
    (
      onBatch: (batch: OrgOption[]) => void,
      shouldContinue: () => boolean = () => true,
    ) => fetchPage(onBatch, shouldContinue),
    [fetchPage],
  );

  return { getOrgAsOptionsOfUser };
};
